import React, { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Loader2, Menu, Bell, Settings, HelpCircle, 
  Maximize2, Minimize2, WifiOff, Battery,
  Moon, Sun, ChevronLeft, ChevronRight, Activity,
  Shield, AlertTriangle, CheckCircle, RefreshCw,
  Home, BarChart3, Users, FileText, Map, Mic,
  PanelLeftClose, Camera, X
} from 'lucide-react';

// Lazy load heavy components
const Header = React.lazy(() => import('./components/Header/Header').then(m => ({ default: m.Header })));
const Sidebar = React.lazy(() => import('./components/Sidebar/ProjectInfoSidebar').then(m => ({ default: m.ProjectInfoSidebar })));
const MainContentArea = React.lazy(() => import('./components/MainContent/MainContentArea'));
const RightPanel = React.lazy(() => import('./components/RightPanel/RightPanel').then(m => ({ default: m.RightPanel })));

import { DashboardProvider, useDashboard } from './context/DashboardContext';
import { ThemeWrapper } from './ThemeWrapper';
import IchthysBibleWidget from '../IchthysBibleWidget'; // ADD THIS IMPORT

// Types
interface DashboardConfig {
  theme: 'light' | 'dark' | 'auto';
  sidebarPosition: 'left' | 'right';
  rightPanelEnabled: boolean;
  compactMode: boolean;
  animations: boolean;
  soundEffects: boolean;
}

interface SystemStatus {
  isOnline: boolean;
  batteryLevel: number | null;
  connectionQuality: 'excellent' | 'good' | 'poor' | 'offline';
  lastSync: Date;
  pendingUploads: number;
}

interface LoadingStep {
  id: string;
  label: string;
  status: 'pending' | 'loading' | 'complete' | 'error';
}

// Custom hooks
const useSystemStatus = (): SystemStatus => {
  const [status, setStatus] = useState<SystemStatus>({
    isOnline: navigator.onLine,
    batteryLevel: null,
    connectionQuality: navigator.onLine ? 'excellent' : 'offline',
    lastSync: new Date(),
    pendingUploads: 0
  });

  useEffect(() => {
    const handleOnline = () => setStatus(prev => ({ ...prev, isOnline: true, connectionQuality: 'good' }));
    const handleOffline = () => setStatus(prev => ({ ...prev, isOnline: false, connectionQuality: 'offline' }));

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Battery status
    if ('getBattery' in navigator) {
      (navigator as any).getBattery().then((battery: any) => {
        setStatus(prev => ({ ...prev, batteryLevel: battery.level * 100 }));
      });
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return status;
};

const useKeyboardShortcuts = (callbacks: Record<string, () => void>) => {
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Command/Ctrl + B - Toggle sidebar
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        callbacks.toggleSidebar?.();
      }
      // Command/Ctrl + / - Help
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        callbacks.openHelp?.();
      }
      // Escape - Close modals
      if (e.key === 'Escape') {
        callbacks.closeModals?.();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [callbacks]);
};

// Error Boundary
class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ComponentType<{ error: Error }> },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      const Fallback = this.props.fallback || DefaultErrorFallback;
      return <Fallback error={this.state.error!} />;
    }

    return this.props.children;
  }
}

const DefaultErrorFallback: React.FC<{ error: Error }> = ({ error }) => (
  <div className="h-screen w-full flex items-center justify-center bg-gray-900 overflow-hidden">
    <div className="text-center max-w-md">
      <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-white mb-2">Something went wrong</h2>
      <p className="text-gray-400 mb-4">{error.message}</p>
      <button 
        onClick={() => window.location.reload()} 
        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        Reload Dashboard
      </button>
    </div>
  </div>
);

// Enhanced Loading Screen
const LoadingScreen: React.FC<{ steps: LoadingStep[] }> = ({ steps }) => {
  const [currentMessage, setCurrentMessage] = useState(0);
  const messages = [
    "Initializing construction platform...",
    "Loading project data...",
    "Connecting to field devices...",
    "Syncing vendor information...",
    "Preparing 3D visualizations..."
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessage(prev => (prev + 1) % messages.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [messages.length]);

  return (
    <div className="h-screen w-full bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center overflow-hidden">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-2xl w-full px-8"
      >
        {/* Logo Animation - Magnifying Glass Zooming */}
        <div className="relative w-48 h-48 mx-auto mb-8">
          <motion.svg
            viewBox="0 0 120 120"
            className="w-full h-full"
            style={{ filter: 'drop-shadow(0 4px 20px rgba(6, 182, 212, 0.6))' }}
          >
            {/* Magnifying Glass Lens Group */}
            <motion.g
              animate={{
                scale: [1, 1.12, 1],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              style={{ transformOrigin: '50px 50px' }}
            >
              {/* Glass lens with gradient */}
              <circle
                cx="50"
                cy="50"
                r="32"
                fill="url(#lensGradientLoading)"
                stroke="#06b6d4"
                strokeWidth="4"
              />

              {/* Lens highlight */}
              <ellipse
                cx="40"
                cy="40"
                rx="14"
                ry="10"
                fill="rgba(255, 255, 255, 0.35)"
                transform="rotate(-30 40 40)"
              />

              {/* Professional House Icon */}
              <g transform="translate(50, 50)">
                {/* House body */}
                <rect x="-14" y="2" width="28" height="20" fill="#0891b2" stroke="#06b6d4" strokeWidth="1.5"/>

                {/* Roof with ridge */}
                <path d="M -16,2 L 0,-13 L 16,2 Z" fill="#0e7490" stroke="#06b6d4" strokeWidth="1.5"/>
                <line x1="0" y1="-13" x2="0" y2="2" stroke="#06b6d4" strokeWidth="1.2"/>

                {/* Chimney */}
                <rect x="7" y="-10" width="5" height="8" fill="#0e7490" stroke="#06b6d4" strokeWidth="0.8"/>

                {/* Door - centered */}
                <rect x="-4" y="12" width="8" height="10" fill="#164e63" stroke="#06b6d4" strokeWidth="0.8" rx="0.5"/>
                <circle cx="-1" cy="17" r="0.7" fill="#06b6d4"/>

                {/* Windows - two on top with panes */}
                <rect x="-12" y="6" width="6" height="6" fill="#164e63" stroke="#06b6d4" strokeWidth="0.8" rx="0.4"/>
                <line x1="-12" y1="9" x2="-6" y2="9" stroke="#06b6d4" strokeWidth="0.5"/>
                <line x1="-9" y1="6" x2="-9" y2="12" stroke="#06b6d4" strokeWidth="0.5"/>

                <rect x="6" y="6" width="6" height="6" fill="#164e63" stroke="#06b6d4" strokeWidth="0.8" rx="0.4"/>
                <line x1="6" y1="9" x2="12" y2="9" stroke="#06b6d4" strokeWidth="0.5"/>
                <line x1="9" y1="6" x2="9" y2="12" stroke="#06b6d4" strokeWidth="0.5"/>
              </g>
            </motion.g>

            {/* Animated Handle */}
            <motion.g
              animate={{
                rotate: [-2, 2, -2],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              style={{ transformOrigin: '72px 72px' }}
            >
              <line
                x1="72"
                y1="72"
                x2="102"
                y2="102"
                stroke="#0891b2"
                strokeWidth="9"
                strokeLinecap="round"
              />
              <line
                x1="72"
                y1="72"
                x2="102"
                y2="102"
                stroke="#06b6d4"
                strokeWidth="6"
                strokeLinecap="round"
              />
            </motion.g>

            {/* Gradient Definition */}
            <defs>
              <radialGradient id="lensGradientLoading">
                <stop offset="0%" stopColor="rgba(6, 182, 212, 0.2)" />
                <stop offset="100%" stopColor="rgba(6, 182, 212, 0.08)" />
              </radialGradient>
            </defs>
          </motion.svg>
        </div>

        <h1 className="text-4xl font-bold text-white mb-2">HomeQuest Tech</h1>
        <p className="text-gray-400 mb-8">Where Land Meets Logic</p>

        {/* Loading Steps */}
        <div className="space-y-3 mb-8">
          {steps.map((step, index) => (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-center gap-3 text-left"
            >
              {step.status === 'complete' ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : step.status === 'loading' ? (
                <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
              ) : step.status === 'error' ? (
                <AlertTriangle className="w-5 h-5 text-red-500" />
              ) : (
                <div className="w-5 h-5 rounded-full border-2 border-gray-600" />
              )}
              <span className={`text-sm ${
                step.status === 'complete' ? 'text-green-400' :
                step.status === 'loading' ? 'text-white' :
                step.status === 'error' ? 'text-red-400' :
                'text-gray-500'
              }`}>
                {step.label}
              </span>
            </motion.div>
          ))}
        </div>

        {/* Animated Loading Message */}
        <motion.p
          key={currentMessage}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="text-gray-400 text-sm"
        >
          {messages[currentMessage]}
        </motion.p>

        {/* Progress Bar */}
        <div className="mt-8 w-full max-w-xs mx-auto">
          <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-blue-500 to-blue-600"
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 3, ease: "easeInOut" }}
            />
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Mobile Dashboard Component
// Main Dashboard Component
// Inner dashboard component that uses the context
function DashboardContent() {
  const { theme, setTheme } = useDashboard();
  // Add global CSS for hiding scrollbars and removing borders
  useEffect(() => {
    // Force remove borders from ALL elements
    const removeAllBorders = () => {
      const html = document.documentElement;
      const body = document.body;
      const root = document.getElementById('root');

      [html, body, root].forEach(el => {
        if (el) {
          el.style.margin = '0';
          el.style.padding = '0';
          el.style.border = 'none';
          el.style.outline = 'none';
          el.style.boxShadow = 'none';
          el.style.width = '100%';
          el.style.height = '100%';
          el.style.maxWidth = '100%';
          el.style.maxHeight = '100%';
          el.style.minWidth = '100%';
          el.style.minHeight = '100%';
        }
      });
    };

    removeAllBorders();

    // Log diagnostic info
    console.log('HTML width:', document.documentElement.offsetWidth);
    console.log('Body width:', document.body.offsetWidth);
    console.log('Root width:', document.getElementById('root')?.offsetWidth);
    console.log('Window width:', window.innerWidth);

    const style = document.createElement('style');
    style.textContent = `
      /* AGGRESSIVE border removal and max-width override */
      *, *::before, *::after {
        outline: none !important;
      }

      /* Override ANY max-width constraints */
      .container, .mx-auto {
        max-width: 100% !important;
      }

      /* Remove ALL borders, margins, padding from root elements */
      html {
        margin: 0 !important;
        padding: 0 !important;
        border: 0 !important;
        outline: 0 !important;
        box-sizing: border-box !important;
        width: 100% !important;
        height: 100% !important;
        overflow: hidden !important;
        box-shadow: none !important;
      }

      body {
        margin: 0 !important;
        padding: 0 !important;
        border: 0 !important;
        outline: 0 !important;
        box-sizing: border-box !important;
        width: 100% !important;
        height: 100% !important;
        overflow: hidden !important;
        box-shadow: none !important;
      }

      #root {
        margin: 0 !important;
        padding: 0 !important;
        border: 0 !important;
        outline: 0 !important;
        box-sizing: border-box !important;
        width: 100% !important;
        height: 100% !important;
        overflow: hidden !important;
        box-shadow: none !important;
      }

      /* Hide ALL scrollbars globally */
      * {
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE and Edge */
      }
      
      /* Hide scrollbar for Chrome, Safari and Opera */
      *::-webkit-scrollbar {
        display: none;
      }
      
      /* Ensure smooth scrolling */
      * {
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
      }
      
      /* Optional: Add smooth scrolling for specific containers */
      .smooth-scroll {
        overflow-y: auto;
        overflow-x: hidden;
        scrollbar-width: none;
        -ms-overflow-style: none;
        will-change: scroll-position;
      }
      
      .smooth-scroll::-webkit-scrollbar {
        display: none;
      }
      
      /* Ensure body and html don't show scrollbars */
      html, body {
        overflow: hidden;
        scrollbar-width: none;
        -ms-overflow-style: none;
      }
      
      html::-webkit-scrollbar, body::-webkit-scrollbar {
        display: none;
      }
      
      /* Apply to all potential scrollable containers */
      div[class*="overflow-"], 
      div[class*="overflow-y-"], 
      div[class*="overflow-x-"] {
        scrollbar-width: none;
        -ms-overflow-style: none;
        -webkit-overflow-scrolling: touch; /* Momentum scrolling on iOS */
      }
      
      div[class*="overflow-"]::-webkit-scrollbar,
      div[class*="overflow-y-"]::-webkit-scrollbar,
      div[class*="overflow-x-"]::-webkit-scrollbar {
        display: none;
      }
      
      /* Optional: Add subtle scroll indicators on hover for desktop */
      @media (hover: hover) {
        .scroll-indicator {
          position: relative;
        }
        
        .scroll-indicator::after {
          content: '';
          position: absolute;
          right: 2px;
          top: 10%;
          bottom: 10%;
          width: 2px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 1px;
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        
        .scroll-indicator:hover::after {
          opacity: 1;
        }
      }
      
      /* Focus styles for keyboard navigation */
      [tabindex="0"]:focus {
        outline: 2px solid rgba(59, 130, 246, 0.5);
        outline-offset: 2px;
      }
      
      /* Performance optimization for scrolling */
      .overflow-auto, .overflow-y-auto, .overflow-x-auto {
        will-change: scroll-position;
      }
    `;
    document.head.appendChild(style);
    
    // Also add to body directly
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    
    return () => {
      document.head.removeChild(style);
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [loadingSteps, setLoadingSteps] = useState<LoadingStep[]>([
    { id: 'auth', label: 'Authenticating user', status: 'pending' },
    { id: 'projects', label: 'Loading projects', status: 'pending' },
    { id: 'vendors', label: 'Syncing vendor data', status: 'pending' },
    { id: '3d', label: 'Initializing 3D engine', status: 'pending' },
    { id: 'offline', label: 'Preparing offline mode', status: 'pending' }
  ]);
  
  const [config, setConfig] = useState<DashboardConfig>({
    theme: 'dark',
    sidebarPosition: 'left',
    rightPanelEnabled: true,
    compactMode: false,
    animations: true,
    soundEffects: true
  });

  // Initialize sidebar based on screen size
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth < 1024;
  });
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth < 1400;
  });
  const [isFullscreen, setIsFullscreen] = useState(false);

  const systemStatus = useSystemStatus();
  const mainContentRef = useRef<HTMLDivElement>(null);

  // Simulate realistic loading
  useEffect(() => {
    const loadApp = async () => {
      // Simulate loading each step
      for (let i = 0; i < loadingSteps.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setLoadingSteps(prev => prev.map((step, index) =>
          index === i ? { ...step, status: 'loading' } :
          index < i ? { ...step, status: 'complete' } : step
        ));

        await new Promise(resolve => setTimeout(resolve, 300));
        setLoadingSteps(prev => prev.map((step, index) =>
          index <= i ? { ...step, status: 'complete' } : step
        ));
      }

      await new Promise(resolve => setTimeout(resolve, 500));
      setIsLoading(false);
    };

    loadApp();
  }, []);

  // Auto-collapse sidebars on smaller screens and handle resize
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;

      // Auto-collapse sidebars on smaller screens but allow users to toggle them
      if (width < 1024) {
        // On mobile/tablet, start with sidebar collapsed
        setSidebarCollapsed(true);
        setRightPanelCollapsed(true);
      } else if (width < 1400) {
        // On medium screens, keep left sidebar toggleable, collapse right panel
        setRightPanelCollapsed(true);
      } else {
        // On larger screens, show right panel
        setRightPanelCollapsed(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    toggleSidebar: () => setSidebarCollapsed(prev => !prev),
    openHelp: () => console.log('Open help')
  });

  // Add arrow key navigation for scrolling
  useEffect(() => {
    const handleKeyScroll = (e: KeyboardEvent) => {
      const activeElement = document.activeElement;
      const scrollContainer = mainContentRef.current;
      
      if (scrollContainer && !['input', 'textarea'].includes(activeElement?.tagName.toLowerCase() || '')) {
        const scrollAmount = 100;
        
        switch(e.key) {
          case 'ArrowUp':
            scrollContainer.scrollBy({ top: -scrollAmount, behavior: 'smooth' });
            break;
          case 'ArrowDown':
            scrollContainer.scrollBy({ top: scrollAmount, behavior: 'smooth' });
            break;
          case 'PageUp':
            scrollContainer.scrollBy({ top: -window.innerHeight * 0.8, behavior: 'smooth' });
            break;
          case 'PageDown':
            scrollContainer.scrollBy({ top: window.innerHeight * 0.8, behavior: 'smooth' });
            break;
          case 'Home':
            if (e.ctrlKey || e.metaKey) {
              scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
            }
            break;
          case 'End':
            if (e.ctrlKey || e.metaKey) {
              scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' });
            }
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyScroll);
    return () => window.removeEventListener('keydown', handleKeyScroll);
  }, []);

  // Fix sidebar scrolling after mount
  useEffect(() => {
    const fixSidebarScroll = () => {
      // FIXED: Cast to HTMLElement to access style property
      const sidebarContainer = document.querySelector('aside.order-1 > div') as HTMLElement;
      if (sidebarContainer) {
        // Remove any inline styles that might interfere with scrolling
        sidebarContainer.style.cssText = '';

        // Check if content is being cut off
        const scrollHeight = sidebarContainer.scrollHeight;
        const clientHeight = sidebarContainer.clientHeight;

        if (scrollHeight > clientHeight) {
          console.log('Sidebar content is scrollable. Height:', scrollHeight, 'Visible:', clientHeight);
        }
      }
    };

    // Run immediately and after a delay to catch any dynamic content
    fixSidebarScroll();
    const timeout = setTimeout(fixSidebarScroll, 500);

    // Also run on window resize
    window.addEventListener('resize', fixSidebarScroll);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', fixSidebarScroll);
    };
  }, [sidebarCollapsed]);

  // Fullscreen handling
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  // Play sound effect
  const playSound = useCallback((sound: 'click' | 'success' | 'error') => {
    if (config.soundEffects) {
      // In production, play actual sounds
      console.log(`Playing ${sound} sound`);
    }
  }, [config.soundEffects]);

  // Loading Screen
  if (isLoading) {
    return <LoadingScreen steps={loadingSteps} />;
  }

  // Unified Dashboard - Same layout for mobile and desktop
  return (
    <ErrorBoundary>
        <div
          className={`h-screen w-full flex flex-col overflow-hidden ${
            theme === 'dark'
              ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-black'
              : 'bg-gradient-to-br from-gray-50 via-white to-gray-100'
          }`}
        >
          {/* System Status Bar */}
          {(!systemStatus.isOnline || (systemStatus.batteryLevel && systemStatus.batteryLevel < 20)) && (
            <motion.div
              initial={{ y: -40 }}
              animate={{ y: 0 }}
              className="bg-yellow-500 text-black px-4 py-2 text-sm font-medium flex items-center justify-center gap-4 flex-shrink-0"
            >
              {!systemStatus.isOnline && (
                <span className="flex items-center gap-2">
                  <WifiOff className="w-4 h-4" />
                  Offline Mode - Changes will sync when connected
                </span>
              )}
              {systemStatus.batteryLevel && systemStatus.batteryLevel < 20 && (
                <span className="flex items-center gap-2">
                  <Battery className="w-4 h-4" />
                  Low Battery: {Math.round(systemStatus.batteryLevel)}%
                </span>
              )}
            </motion.div>
          )}

          {/* Header - Desktop Only */}
          <motion.header
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ type: "spring", stiffness: 100 }}
            className="flex-shrink-0 z-50 relative lg:block hidden"
          >
            <Suspense fallback={<div className="h-16 bg-gray-800" />}>
              <Header onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />
            </Suspense>
          </motion.header>

          {/* Quick Actions Bar - Always visible */}
          <div className={`flex-shrink-0 z-50 px-4 py-2 flex items-center justify-between ${
            theme === 'dark'
              ? 'bg-gray-800'
              : 'bg-white'
          }`}>
            {/* Left side - Hamburger menu (mobile only) */}
            <div className="flex items-center lg:hidden">
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className={`p-1.5 rounded-lg transition-colors ${
                  theme === 'dark'
                    ? 'hover:bg-gray-700'
                    : 'hover:bg-gray-100'
                }`}
              >
                <Menu className={`w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-700'}`} />
              </button>
            </div>

            {/* Right side - Action buttons */}
            <div className="flex items-center gap-2 ml-auto">
              {systemStatus.pendingUploads > 0 && (
                <span className="flex items-center gap-2 text-sm text-yellow-400">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span className="hidden lg:inline">{systemStatus.pendingUploads} pending</span>
                </span>
              )}

              <button
                onClick={toggleFullscreen}
                className={`p-1.5 rounded-lg transition-colors ${
                  theme === 'dark'
                    ? 'hover:bg-gray-700'
                    : 'hover:bg-gray-100'
                }`}
              >
                {isFullscreen ?
                  <Minimize2 className={`w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-700'}`} /> :
                  <Maximize2 className={`w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-700'}`} />
                }
              </button>

              {/* Ichthys Bible Widget - Next to expand button */}
              <div className="inline-flex">
                <IchthysBibleWidget />
              </div>

              <button
                onClick={() => {
                  const newTheme = theme === 'dark' ? 'light' : 'dark';
                  setTheme(newTheme);
                  setConfig(prev => ({ ...prev, theme: newTheme }));
                }}
                className={`p-1.5 rounded-lg transition-colors ${
                  theme === 'dark'
                    ? 'hover:bg-gray-700'
                    : 'hover:bg-gray-100'
                }`}
              >
                {theme === 'dark' ?
                  <Sun className="w-5 h-5 text-yellow-400" /> :
                  <Moon className="w-5 h-5 text-blue-600" />
                }
              </button>
            </div>
          </div>

          {/* Main Content Area - RESPONSIVE LAYOUT */}
          <div className="flex-1 flex overflow-hidden relative">
            {/* Mobile Overlay when sidebar is open - Only show on screens < 1024px */}
            {!sidebarCollapsed && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSidebarCollapsed(true)}
                className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              />
            )}

            {/* Left Sidebar - Overlay on mobile, fixed width on desktop */}
            <AnimatePresence mode="wait">
              {!sidebarCollapsed && (
                <motion.aside
                  key="left-sidebar"
                  initial={{ x: -300, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -300, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  className={`${
                    theme === 'dark'
                      ? 'bg-gray-800'
                      : 'bg-gray-50'
                  } ${
                    config.sidebarPosition === 'right' ? 'order-3' : 'order-1'
                  } fixed left-0 top-0 z-50 w-[280px] lg:relative lg:w-[300px]`}
                  style={{
                    overflow: 'hidden',
                    height: '100vh',
                    maxHeight: '100vh'
                  }}
                >
                  <div
                    className="h-full flex flex-col overflow-hidden relative"
                    style={{
                      WebkitOverflowScrolling: 'touch'
                    }}
                  >
                    <Suspense fallback={<div className="h-full bg-gray-800 animate-pulse" />}>
                      <Sidebar isCollapsed={false} onToggleCollapse={() => setSidebarCollapsed(true)} />
                    </Suspense>
                  </div>
                </motion.aside>
              )}
            </AnimatePresence>
            
            {/* Main Content - Flexible width */}
            <motion.main
              ref={mainContentRef}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className={`flex-1 overflow-auto order-2 min-w-0 relative focus:outline-none ${
                theme === 'dark'
                  ? 'bg-gray-900'
                  : 'bg-white'
              }`}
              tabIndex={0}
            >
              {/* Floating sidebar toggle when collapsed */}
              {sidebarCollapsed && (
                <motion.button
                  initial={{ x: -100 }}
                  animate={{ x: 0 }}
                  exit={{ x: -100 }}
                  onClick={() => setSidebarCollapsed(false)}
                  className={`hidden lg:block absolute left-4 top-4 z-20 p-2 rounded-lg shadow-lg transition-colors ${
                    theme === 'dark'
                      ? 'bg-gray-800 hover:bg-gray-700'
                      : 'bg-white hover:bg-gray-50'
                  }`}
                  title="Show Sidebar (Cmd+B)"
                >
                  <PanelLeftClose className={`w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-700'}`} />
                </motion.button>
              )}
              <Suspense fallback={
                <div className="h-full flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                </div>
              }>
                <MainContentArea />
              </Suspense>
            </motion.main>
            
            {/* Right Panel - Hidden on mobile, collapsible on desktop */}
            {config.rightPanelEnabled && (
              <AnimatePresence>
                <motion.aside
                  key="right-sidebar"
                  initial={false}
                  animate={{
                    width: rightPanelCollapsed ? 64 : 380,
                    opacity: 1
                  }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  className={`overflow-hidden order-3 flex-shrink-0 relative hidden lg:block ${
                    theme === 'dark'
                      ? 'bg-gray-800'
                      : 'bg-gray-50'
                  }`}
                >
                  <button
                    onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full bg-gray-800 p-1 rounded-l-lg z-10 hover:bg-gray-700 transition-colors"
                  >
                    {rightPanelCollapsed ? <ChevronLeft className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                  </button>
                  <div className="h-full overflow-y-auto overflow-x-hidden">
                    <Suspense fallback={<div className="h-full bg-gray-800 animate-pulse" />}>
                      <RightPanel isCollapsed={rightPanelCollapsed} />
                    </Suspense>
                  </div>
                </motion.aside>
              </AnimatePresence>
            )}
          </div>

          {/* Mobile Navigation - REMOVED, using hamburger menu instead */}
          {/* Status Bar - Removed for cleaner mobile experience */}

        </div>
        
    </ErrorBoundary>
  );
}

// Main component that provides the context
export default function HomeQuestDashboard() {
  return (
    <DashboardProvider>
      <ThemeWrapper>
        <DashboardContent />
      </ThemeWrapper>
    </DashboardProvider>
  );
}